<template>
  <v-container>
    <v-row v-if="canShowPlanningNotification">
      <v-expand-transition>
      <v-alert
          v-show="showPlanningNotification"
          type="warning"
          closable
          class="ma-5"
      >
        {{$t('mission-planning.warning.readings-older-than-current-week-to-finish')}}
      </v-alert>
      </v-expand-transition>
    </v-row>
    <v-row>
      <v-col cols="12" md="12">
        <span
          class="text-h5 font-weight-bold"
          :style="{'color':appStyle.title_text_color}"
        >
          {{ $t('global.title.planning') }} {{filter.selectedYear}}
        </span>

        <v-btn
          v-if="!calendarViewEnabled"
          :prepend-icon="mdiCalendar"
          :title="$t('appointment.new.btn.title')"
          :aria-label="$t('appointment.new.btn.title')"
          class="float-right mx-2"
          v-bind="useButtonStyle"
          @click="()=>updateCalendarView(true)"
        >
          {{ $t('planning.views.calendar-view') }}
        </v-btn>

        <v-btn
          v-if="calendarViewEnabled"
          :prepend-icon="mdiTimelineOutline"
          :title="$t('appointment.new.btn.title')"
          :aria-label="$t('appointment.new.btn.title')"
          class="float-right mx-2"
          v-bind="useButtonStyle"
          @click="()=>updateCalendarView(false)"
        >
          {{ $t('planning.views.timeline-view') }}
        </v-btn>

        <visit-new-modal v-if="hasAtLeastOnePlanningTypeFree">
          <template #trigger="slotProps">
            <v-btn
              :prepend-icon="mdiCalendarPlus"
              :title="$t('appointment.new.btn.title')"
              :aria-label="$t('appointment.new.btn.title')"
              @click="slotProps.triggerAction"
              class="float-right mx-2"
              v-bind="useButtonStyle"
            >
              {{ $t('appointment.new.btn.title') }}
            </v-btn>
          </template>
        </visit-new-modal>

        <event-new-modal>
          <template #trigger="slotProps">
            <v-btn
              :prepend-icon="mdiCalendarStar"
              :title="$t('event.new.btn.title')"
              :aria-label="$t('event.new.btn.title')"
              @click="slotProps.triggerAction"
              class="float-right mx-2"
              v-bind="useButtonStyle"
            >
              {{ $t('event.new.btn.title') }}
            </v-btn>
          </template>
        </event-new-modal>
      </v-col>

      <v-col cols="12" md="12">
        <mission-planning-calendar
          v-if="calendarViewEnabled"
          :missions="missions"
          :missionsPlannings="missionsPlannings"
          :pointOfSales="pointOfSales"
          :readingsHistory="readingsHistory"
          :readings="readings"
          :hasAtLeastOnePlanningTypeFree="hasAtLeastOnePlanningTypeFree"
          :planningCras="planningCras"
          :tagPosCatalog="allTagPosCatalog"
          />
        <missions-list
          v-else
          :missions="missions"
          :missionsPlannings="missionsPlannings"
          :planningCras="planningCras"
          :pointOfSales="pointOfSales"
          :readingsHistory="readingsHistory"
          :filter="filter"
        />
      </v-col>
    </v-row>
    <v-bottom-navigation v-if="allTagPosCatalog && Object.keys(allTagPosCatalog).length > 0">
      <tag-pos-legend :tag-pos-catalog="allTagPosCatalog" />
    </v-bottom-navigation>
  </v-container>
</template>

<script setup>
import {computed, inject, onMounted, ref} from 'vue';
  import { useStoreManager } from '@/stores/storeManager';
  import { mdiCalendarPlus, mdiCalendar, mdiTimelineOutline, mdiCalendarStar } from '@mdi/js';
  import { useApplicationStore } from '@/stores/applicationStore';
  import MissionsList from '@/components/planning/MissionsList.vue';
  import VisitNewModal from '@/components/mission/appointments/VisitNewModal.vue';
  import EventNewModal from '@/components/planning/EventNewModal.vue';
  import { hasAtLeastOnePlanningType } from '@/stores/manager/configurationManager';
  import { SETTINGS } from '@/constants';
  import {reactive} from "vue";
  import moment from 'moment';
  import dateHandler from '@/utils/dateHandler';
  import {useButtonStyle} from "@/utils/appStyleHandler";
import MissionPlanningCalendar from '@/components/planning/MissionPlanningCalendar.vue'
import TagPosLegend from '@/components/common/TagPosLegend.vue'

  const filter = reactive({
    selectedYear: moment().format('Y'),
    selectedWeek: { value: dateHandler.getCurrentWeekMondayAndSunday() }
  });

  const applicationStore = useApplicationStore();
  const storeManager = useStoreManager();
  const isFirstMountAfterLogin = inject('isFirstMountAfterLogin');
  const showPlanningNotification = ref(false);

  const calendarViewEnabled = computed(() => applicationStore.data.calendarViewEnabled)

  const displayPreviousPlanningNotification = () => {
    if (isFirstMountAfterLogin.value) {
      isFirstMountAfterLogin.value = false;
      showPlanningNotification.value = true;
      setTimeout(() => {
        showPlanningNotification.value = false;
      }, "8000");
    }
  };

  const appStyle = computed(() => applicationStore.appStyle);

  const missionsPlannings = computed(() =>
    storeManager.getFromStoreByCategory('mission', 'missionsPlannings')
  );

  const planningCras = computed(() =>
      storeManager.getFromStoreByCategory('mission', 'planningCras')
  );

  const canShowPlanningNotification = computed(() => {
    const startOfWeekUnixTimeStamp = moment().startOf('isoWeek').unix();
    const visitsBeforeCurrentWeekToFinishCount = Object.values(missionsPlannings?.value)?.filter((mp) => {
      return mp.transmittedReading !== true && mp.date_start < startOfWeekUnixTimeStamp;
    })?.length || 0;

    return visitsBeforeCurrentWeekToFinishCount > 0
  });

  const missions = computed(() =>
    storeManager.getFromStoreByCategory('mission', 'missions')
  );

  const pointOfSales = computed(() => {
    const rawPOS = storeManager.getFromStoreByCategory('mission', 'pointsOfSale');
    Object.keys(rawPOS).forEach(function (key) {
      rawPOS[key]["id"] = key;
    });
    return rawPOS;
  });

  const readingsHistory = computed(() =>
    storeManager.getFromStoreByCategory('readingHistory', 'reading')
  );

  const readings = computed(() =>
    storeManager.getFromStoreByCategory('reading', 'reading') || {}
  );

  const configurations = computed(() =>
    storeManager.getFromStoreByCategory('configuration', 'configurations') || {}
  );

  const hasAtLeastOnePlanningTypeFree = computed(() =>
    hasAtLeastOnePlanningType(configurations.value, SETTINGS.PLANNING_TYPE.FREE)
  );

  const allTagPosCatalog = computed(() => {
    const catalog = {};
    Object.values(configurations.value).forEach(conf => {
      if (conf?.catalog?.tag_pos) {
        Object.assign(catalog, conf.catalog.tag_pos);
      }
    });
    return catalog;
  });

  const updateCalendarView = (status) => {
    applicationStore.updateCalendarView(status);
  }

  onMounted(() => {
    displayPreviousPlanningNotification();
  });

</script>
